/**
 * Malha (Fraudes) — spec docs/spec-tarefas-e-malha.md §3.4, §4, §5.1 (fatia 2).
 * Réguas com o painel Ação (setor obrigatório), simulação antes de ligar, motor
 * "Rodar agora", casos do mês por estado e calibração. Balizamento: fatia 3.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Scale, Play, FlaskConical, Pencil, Trash2, History, Plus, CheckCircle2, AlertTriangle, Loader, ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { malhaAPI, setoresAPI, tiposTarefaAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import InfoTip from '../../components/InfoTip';

const card = { background: '#fff', borderRadius: 12, padding: '1.1rem 1.3rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' };
const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #f0f0f0' };
const td = { padding: '8px 10px', fontSize: '0.85rem', borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' };
const num = { ...td, textAlign: 'right', fontFamily: "'SF Mono','JetBrains Mono',Consolas,monospace", whiteSpace: 'nowrap' };
const btn = (primario) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', border: primario ? 'none' : '1px solid var(--gray-200, #e5e7eb)', background: primario ? 'var(--brand, #20a490)' : '#fff', color: primario ? '#fff' : 'var(--gray-700, #333)' });
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MESES = ['', 'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesLabel = (m) => `${MESES[Number(m.slice(5, 7))]}/${m.slice(2, 4)}`;
const ESTADO = {
  NOVO: { bg: '#fdecea', color: '#c0392b', rot: 'Novo', tip: 'Passou da régua e ainda não tem parecer: abre tarefa no setor apontado.' },
  DESVIO: { bg: '#fef5e7', color: '#b9770e', rot: 'Fora do justificado', tip: 'Tinha justificativa aprovada, mas saiu do padrão esperado (faixa ou grupo novo): nova tarefa.' },
  BALIZADO: { bg: '#e8f6f4', color: '#1e8449', rot: 'Justificado', tip: 'Parecer "gasto justificado" aprovado: dentro do padrão esperado, sem tarefa, só acompanhado aqui.' },
  REVISAO: { bg: '#eef2f7', color: '#2c3e50', rot: 'Justificativa vencida', tip: 'A validade do padrão esperado venceu: nova tarefa para revalidar.' },
  INCONSISTENTE: { bg: '#fdecea', color: '#7b241c', rot: 'Inconsistente', tip: 'Parecer "indício de irregularidade": dossiê registrado e tarefa transferida ao setor escolhido.' },
};
const PRIORIDADES = [['normal', 'Normal'], ['alta', 'Alta'], ['urgente', 'Urgente']];

function Estado({ e }) {
  const s = ESTADO[e] || { bg: '#eee', color: '#333', rot: e };
  return <span title={s.tip} style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.rot}</span>;
}

function Simulacao({ medida, limite }) {
  const [dados, setDados] = useState(null);
  const [rodando, setRodando] = useState(false);
  const simular = () => {
    if (!medida || !(Number(limite) > 0)) return toast.error('Escolha a medida e um limite maior que zero');
    setRodando(true);
    malhaAPI.simular(medida, Number(limite), 12).then((r) => setDados(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Falha ao simular')).finally(() => setRodando(false));
  };
  return (
    <div style={{ background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: 8, padding: 10, marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" style={btn(false)} onClick={simular} disabled={rodando}>
          {rodando ? <Loader size={14} className="spin" /> : <FlaskConical size={14} />} Simular nos últimos 12 meses
        </button>
        <InfoTip w={320} texto="Quantos beneficiários cairiam na régua, mês a mês, com esta medida e este limite. Não grava nada nem abre card. Sem simular, não ligue." />
        {dados && <span style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>média <b>{dados.media_mes}</b>/mês · máx <b>{dados.max_mes}</b> · {dados.beneficiarios_distintos} beneficiários distintos · {dados.reincidentes_3_ou_mais} em 3+ meses</span>}
      </div>
      {dados && (
        <div style={{ display: 'flex', gap: 4, marginTop: 8, alignItems: 'flex-end', height: 70 }}>
          {dados.meses.map((m) => {
            const h = dados.max_mes ? Math.max(4, Math.round((m.qt / dados.max_mes) * 56)) : 4;
            return (
              <div key={m.mes} title={`${mesLabel(m.mes)}: ${m.qt} caso(s), R$ ${fmt(m.valor)}`} style={{ flex: 1, textAlign: 'center', fontSize: '0.65rem', color: 'var(--gray-500)' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--gray-700)' }}>{m.qt}</div>
                <div style={{ height: h, background: 'var(--brand, #20a490)', borderRadius: 3, opacity: 0.85 }} />
                <div>{mesLabel(m.mes)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ModalRegua({ regua, medidas, setores, tipos, onClose, onSalvo }) {
  const [r, setR] = useState({ nome: '', ativa: false, medida: medidas[0]?.codigo || '', limite: 9999, tolerancia_balizamento_pct: 10, validade_balizamento_meses: 6, ...(regua || {}) });
  const [a, setA] = useState({ ligada: false, abre: 'tarefa', tipo_tarefa_codigo: 'reembolso_regua', setor_id: '', prioridade: 'normal', ...(regua?.acao || {}) });
  const [salvando, setSalvando] = useState(false);
  const setr = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const seta = (k, v) => setA((x) => ({ ...x, [k]: v }));
  const salvar = async () => {
    if (!r.nome || r.nome.length < 3) return toast.error('Dê um nome à régua');
    if (!(Number(r.limite) > 0)) return toast.error('Limite deve ser maior que zero');
    if (!a.setor_id) return toast.error('Aponte o setor que vai tratar (obrigatório)');
    if (a.abre === 'tarefa' && !a.tipo_tarefa_codigo) return toast.error('Escolha o tipo de tarefa do cardápio');
    setSalvando(true);
    try {
      const reg = { nome: r.nome, ativa: !!r.ativa, medida: r.medida, limite: Number(r.limite), tolerancia_balizamento_pct: Number(r.tolerancia_balizamento_pct), validade_balizamento_meses: Number(r.validade_balizamento_meses) };
      const ac = { ligada: !!a.ligada, abre: a.abre, tipo_tarefa_codigo: a.tipo_tarefa_codigo || null, setor_id: Number(a.setor_id), prioridade: a.prioridade };
      if (regua?.id) await malhaAPI.atualizar(regua.id, { regua: reg, acao: ac }); else await malhaAPI.criar({ regua: reg, acao: ac });
      toast.success('Régua salva');
      onSalvo();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar a régua'); } finally { setSalvando(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Scale size={18} /> {regua?.id ? 'Editar régua' : 'Nova régua'}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
          <div className="form-group"><label>Nome</label><input className="form-control" value={r.nome} onChange={(e) => setr('nome', e.target.value)} placeholder="ex.: Reembolso acima de R$ 9.999 no mês" /></div>
          <div className="form-group"><label>Limite (R$)</label><input className="form-control" type="number" min="1" step="0.01" value={r.limite} onChange={(e) => setr('limite', e.target.value)} /></div>
        </div>
        <div className="form-group">
          <label>O que mede</label>
          <select className="form-control" value={r.medida} onChange={(e) => setr('medida', e.target.value)}>
            {medidas.map((m) => <option key={m.codigo} value={m.codigo}>{m.rotulo}</option>)}
          </select>
          <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 4 }}>{medidas.find((m) => m.codigo === r.medida)?.descricao}</div>
        </div>
        <Simulacao medida={r.medida} limite={r.limite} />

        <div style={{ marginTop: 14, padding: 12, border: '1px solid var(--gray-200, #e5e7eb)', borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            Ação <InfoTip w={340} texto="O mesmo painel para toda fonte (Fraudes hoje, Auditoria Bancária depois). O destino é sempre apontado aqui: sem setor a régua não salva. Registrar na Malha toda régua ativa faz; abrir card é o que a Ação ligada faz." />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
            <div className="form-group">
              <label>Setor que trata <span style={{ color: '#c0392b' }}>*</span></label>
              <select className="form-control" value={a.setor_id || ''} onChange={(e) => seta('setor_id', e.target.value)}>
                <option value="">Selecione…</option>
                {setores.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Tipo de tarefa (cardápio)</label>
              <select className="form-control" value={a.tipo_tarefa_codigo || ''} onChange={(e) => seta('tipo_tarefa_codigo', e.target.value)}>
                <option value="">Selecione…</option>
                {tipos.map((t) => <option key={t.codigo} value={t.codigo}>{t.nome}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Prioridade (SLA vem do setor)</label>
              <select className="form-control" value={a.prioridade} onChange={(e) => seta('prioridade', e.target.value)}>
                {PRIORIDADES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18, fontSize: '0.85rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!r.ativa} onChange={(e) => setr('ativa', e.target.checked)} /> Régua ativa (o motor roda e registra na Malha)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!a.ligada} onChange={(e) => seta('ligada', e.target.checked)} /> Abrir tarefa no setor para cada caso
            </label>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: 10 }}>
          <div className="form-group"><label>Tolerância do balizamento (%)</label><input className="form-control" type="number" min="0" max="100" value={r.tolerancia_balizamento_pct} onChange={(e) => setr('tolerancia_balizamento_pct', e.target.value)} /></div>
          <div className="form-group"><label>Validade do balizamento (meses)</label><input className="form-control" type="number" min="1" max="60" value={r.validade_balizamento_meses} onChange={(e) => setr('validade_balizamento_meses', e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={salvando}>Cancelar</button>
          <button className="btn btn-success" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar régua'}</button>
        </div>
      </div>
    </div>
  );
}

export default function AbaMalha() {
  const { temNivel } = useAuth();
  const gestor = temNivel('radar_carteira', 'gestor');
  const navigate = useNavigate();
  const [mes, setMes] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; });
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [setores, setSetores] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [modal, setModal] = useState(null);      // null | {} (nova) | regua
  const [hist, setHist] = useState({});          // regua_id -> lista
  const [rodando, setRodando] = useState(false);
  const meses = useMemo(() => { const out = []; const d = new Date(); for (let i = 0; i < 12; i++) { out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); d.setMonth(d.getMonth() - 1); } return out; }, []);

  const carregar = () => {
    setErro('');
    malhaAPI.resumo(mes).then((r) => setDados(r.data)).catch((e) => setErro(e.response?.data?.detail || 'Falha ao carregar a Malha'));
  };
  useEffect(() => { carregar(); }, [mes]);
  useEffect(() => {
    setoresAPI.list().then((r) => setSetores(r.data || [])).catch(() => {});
    tiposTarefaAPI.list({ ativos: true }).then((r) => setTipos(r.data || [])).catch(() => {});
  }, []);

  const rodar = (reguaId) => {
    setRodando(true);
    malhaAPI.rodar({ mes, regua_id: reguaId })
      .then((r) => {
        if (r.data.ignorada) return toast(r.data.motivo);
        const cards = r.data.execucoes.reduce((s, e) => s + (e.qt_cards || 0), 0);
        const casos = r.data.execucoes.reduce((s, e) => s + (e.qt_casos || 0), 0);
        const erros = r.data.execucoes.filter((e) => !e.ok);
        if (erros.length) toast.error(`Régua com erro: ${erros[0].erro}`); else toast.success(`${casos} caso(s) na régua, ${cards} tarefa(s) aberta(s)/atualizada(s)`);
        if (r.data.desligadas_por_setor_inativo?.length) toast.error(`Régua desligada: setor desativado (${r.data.desligadas_por_setor_inativo.map((d) => d.nome).join(', ')})`);
        carregar();
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Falha ao rodar'))
      .finally(() => setRodando(false));
  };
  const excluir = (r) => {
    if (!window.confirm(`Excluir a régua "${r.nome}"? Os casos já registrados somem da Malha; as tarefas abertas ficam.`)) return;
    malhaAPI.excluir(r.id).then(() => { toast.success('Régua excluída'); carregar(); }).catch((e) => toast.error(e.response?.data?.detail || 'Erro'));
  };
  const verHistorico = (r) => {
    if (hist[r.id]) return setHist((h) => ({ ...h, [r.id]: null }));
    malhaAPI.historico(r.id).then((res) => setHist((h) => ({ ...h, [r.id]: res.data }))).catch(() => toast.error('Falha ao carregar histórico'));
  };

  if (erro) return <div style={{ ...card, color: '#c62828' }}><AlertTriangle size={16} /> {erro}</div>;
  if (!dados) return <div style={{ ...card, textAlign: 'center', color: 'var(--gray-400)' }}><Loader size={24} className="spin" /><div>Carregando a Malha…</div></div>;

  const reguas = dados.reguas || [];
  const ativas = reguas.filter((r) => r.ativa);
  const casos = dados.casos || [];
  const kpi = (label, value, sub, tip) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: '1rem 1.2rem', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', flex: '1 1 160px', minWidth: 150 }}>
      <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', fontWeight: 600, display: 'flex', alignItems: 'center' }}>{label}{tip && <InfoTip w={300} texto={tip} />}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--gray-800)' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}><Scale size={18} /> Malha de réguas</h3>
        <InfoTip w={360} texto="Réguas são regras como dado: medida + limite + Ação (setor obrigatório). O motor roda todo dia depois das contas médicas (mês corrente e anterior). Cada caso novo vira tarefa no setor; lá o operador analisa as contas e registra o parecer: gasto justificado (padrão esperado aprovado pelo gestor, sem novas tarefas enquanto estiver dentro dele), indício de irregularidade (dossiê e transferência) ou inconclusivo." />
        <select value={mes} onChange={(e) => setMes(e.target.value)} className="form-control" style={{ width: 'auto', marginLeft: 'auto' }}>
          {meses.map((m) => <option key={m} value={m}>{mesLabel(m)}</option>)}
        </select>
        {gestor && <button style={btn(false)} onClick={() => rodar(null)} disabled={rodando || !ativas.length} title="Roda as réguas ativas para o mês escolhido">{rodando ? <Loader size={14} className="spin" /> : <Play size={14} />} Rodar agora</button>}
        {gestor && <button style={btn(true)} onClick={() => setModal({})}><Plus size={14} /> Nova régua</button>}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        {kpi('Réguas ativas', ativas.length, `${reguas.length} cadastrada(s)`)}
        {kpi('Novos no mês', dados.por_estado?.NOVO || 0, mesLabel(mes), 'Passaram da régua sem balizamento: viraram tarefa (se a Ação estiver ligada).')}
        {kpi('Fora do justificado', dados.por_estado?.DESVIO || 0, 'saíram do padrão esperado')}
        {kpi('Justificados', dados.por_estado?.BALIZADO || 0, 'dentro do padrão, sem tarefa')}
        {kpi('Inconsistentes', dados.por_estado?.INCONSISTENTE || 0, 'indício de irregularidade')}
        {kpi('Tarefas abertas', casos.filter((c) => c.protocolo && c.status !== 'finalizado' && c.status !== 'FINALIZADO').length, 'no mês')}
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>Réguas</div>
        {reguas.length === 0 ? (
          <div style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>Nenhuma régua ainda. {gestor ? 'Crie a primeira em "Nova régua", simule e só então ligue.' : 'Peça ao gestor do Radar para criar.'}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Régua</th><th style={th}>Mede</th><th style={{ ...th, textAlign: 'right' }}>Limite</th><th style={th}>Ação</th><th style={th}>Última execução</th><th style={th} /></tr></thead>
            <tbody>
              {reguas.map((r) => (
                <React.Fragment key={r.id}>
                  <tr style={{ opacity: r.ativa ? 1 : 0.6 }}>
                    <td style={td}><b>{r.nome}</b><div style={{ fontSize: '0.72rem', color: r.ativa ? '#1e8449' : 'var(--gray-500)' }}>{r.ativa ? 'ativa' : 'desligada'}</div></td>
                    <td style={td}>{r.medida_rotulo}</td>
                    <td style={num}>R$ {fmt(r.limite)}</td>
                    <td style={td}>
                      {r.acao ? (
                        <>
                          <div>{r.acao.ligada ? 'abre tarefa' : 'só registra'} · <b>{r.acao.setor_nome || '—'}</b> · {r.acao.prioridade}</div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{r.acao.tipo_tarefa_nome || r.acao.tipo_tarefa_codigo}</div>
                          {r.acao.setor_ativo === false && <div style={{ fontSize: '0.72rem', color: '#c0392b' }}><AlertTriangle size={11} /> setor desativado: escolha outro</div>}
                        </>
                      ) : '—'}
                    </td>
                    <td style={td}>{r.ultima_execucao ? new Date(r.ultima_execucao).toLocaleString('pt-BR') : 'nunca'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {gestor && <button style={{ ...btn(false), padding: '4px 8px', marginRight: 4 }} title="Editar régua e Ação" onClick={() => setModal(r)}><Pencil size={13} /></button>}
                      {gestor && <button style={{ ...btn(false), padding: '4px 8px', marginRight: 4 }} title="Rodar só esta régua no mês" onClick={() => rodar(r.id)} disabled={rodando || !r.ativa}><Play size={13} /></button>}
                      <button style={{ ...btn(false), padding: '4px 8px', marginRight: 4 }} title="Histórico de alterações" onClick={() => verHistorico(r)}><History size={13} /></button>
                      {gestor && <button style={{ ...btn(false), padding: '4px 8px', color: '#c0392b' }} title="Excluir" onClick={() => excluir(r)}><Trash2 size={13} /></button>}
                    </td>
                  </tr>
                  {hist[r.id] && (
                    <tr><td colSpan={6} style={{ ...td, background: '#fafbfc', fontSize: '0.78rem' }}>
                      {hist[r.id].length === 0 ? 'Sem histórico.' : hist[r.id].map((h, i) => (
                        <div key={i}><b>{new Date(h.quando).toLocaleString('pt-BR')}</b> · {h.quem} · {h.origem}: {JSON.stringify(h.de)} → {JSON.stringify(h.para)}</div>
                      ))}
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>Casos em {mesLabel(mes)}<InfoTip w={320} texto="Um caso = régua × beneficiário × mês. Estado NOVO abre tarefa (protocolo TAR-); o resultado da tarefa (confirmado / legítimo / inconclusivo) volta para cá e alimenta a calibração." /></div>
        {casos.length === 0 ? <div style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>Nenhum caso registrado neste mês. {ativas.length ? 'Rode o motor ou espere a execução diária.' : ''}</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Estado</th><th style={th}>Beneficiário</th><th style={th}>Régua</th><th style={{ ...th, textAlign: 'right' }}>Valor</th><th style={{ ...th, textAlign: 'right' }}>Contas</th><th style={th}>Composição</th><th style={th}>Tarefa</th></tr></thead>
              <tbody>
                {casos.map((c) => (
                  <tr key={`${c.regua_id}-${c.matricula_benef}`}>
                    <td style={td}><Estado e={c.estado} />{c.motivo_desvio && <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{c.motivo_desvio}</div>}</td>
                    <td style={td}><div>{c.nome_benef || '—'}</div><div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>{c.matricula_benef}</div></td>
                    <td style={td}>{c.regua}</td>
                    <td style={num}>R$ {fmt(c.valor)}</td>
                    <td style={num}>{c.qt}</td>
                    <td style={{ ...td, fontSize: '0.75rem', color: 'var(--gray-600)' }}>{(c.composicao || []).slice(0, 3).map((x) => `${x.ds_grupo}: ${fmt(x.valor)}`).join(' · ')}</td>
                    <td style={td}>
                      {c.protocolo ? (
                        <button style={{ ...btn(false), padding: '3px 8px', fontSize: '0.75rem' }} onClick={() => navigate(`/atendimento/${c.atendimento_id}`)}>
                          <ExternalLink size={12} /> {c.protocolo}
                        </button>
                      ) : <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>sem card</span>}
                      {c.status && <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{String(c.status).toLowerCase()}{c.resultado ? ` · ${c.resultado}` : ''}</div>}
                      {c.protocolo && (c.estado === 'NOVO' || c.estado === 'REVISAO' || c.estado === 'DESVIO') && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: 2 }}>parecer é registrado na tarefa</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          Justificativas vigentes ({(dados.balizamentos || []).length})
          <InfoTip w={330} texto="Pareceres 'gasto justificado' aprovados, com o padrão esperado. Continuam medidos todo mês; acima do máximo, com grupo novo ou vencidos, voltam a abrir tarefa." />
          {(dados.vencendo_30d || []).length > 0 && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#b9770e' }}><AlertTriangle size={12} /> {dados.vencendo_30d.length} vence(m) em até 30 dias</span>}
        </div>
        {(dados.balizamentos || []).length === 0 ? <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>Nenhuma. Nascem quando um parecer "gasto justificado" é aprovado na tarefa.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Beneficiário</th><th style={th}>Régua</th><th style={{ ...th, textAlign: 'right' }}>Faixa</th><th style={th}>Motivo</th><th style={th}>Válido até</th><th style={th}>Aprovado por</th><th style={th} /></tr></thead>
            <tbody>
              {dados.balizamentos.map((b) => {
                const vence = new Date(b.valido_ate) - new Date() < 30 * 86400000;
                return (
                  <tr key={b.id}>
                    <td style={td}><div>{b.nome || '—'}</div><div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>{b.matricula}</div></td>
                    <td style={td}>{reguas.find((r) => r.id === b.regua_id)?.nome || b.regua_id}</td>
                    <td style={num}>R$ {fmt(b.faixa_min)} – {fmt(b.faixa_max)}</td>
                    <td style={{ ...td, fontSize: '0.8rem' }}>{b.motivo}{b.evidencia && <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>{b.evidencia}</div>}</td>
                    <td style={{ ...td, color: vence ? '#b9770e' : undefined, fontWeight: vence ? 700 : 400 }}>{new Date(b.valido_ate + 'T12:00:00').toLocaleDateString('pt-BR')}</td>
                    <td style={{ ...td, fontSize: '0.78rem' }}>{b.aprovado_por}<div style={{ fontSize: '0.7rem', color: 'var(--gray-500)' }}>{new Date(b.aprovado_em).toLocaleDateString('pt-BR')}</div></td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {gestor && <button style={{ ...btn(false), padding: '3px 8px', fontSize: '0.72rem', color: '#c0392b' }} onClick={() => {
                        const motivo = window.prompt('Motivo da revogação (o caso volta a abrir tarefa quando passar da régua):');
                        if (motivo === null) return;
                        malhaAPI.revogar(b.id, motivo).then(() => { toast.success('Balizamento revogado'); carregar(); }).catch((e) => toast.error(e.response?.data?.detail || 'Erro'));
                      }}>Revogar</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>Calibração (12 meses)<InfoTip w={320} texto="Por régua: quantos casos, quantos viraram balizados e o que as tarefas devolveram. Se metade dos novos volta como legítimo, a régua está baixa: suba o limite ou balize." /></div>
          {(dados.calibracao || []).length === 0 ? <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>Ainda sem execuções.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}>Régua</th><th style={{ ...th, textAlign: 'right' }}>Casos</th><th style={{ ...th, textAlign: 'right' }}>Novos</th><th style={{ ...th, textAlign: 'right' }}>Justificados</th><th style={{ ...th, textAlign: 'right' }}>Finalizadas</th><th style={{ ...th, textAlign: 'right' }}>Parecer justificado</th><th style={{ ...th, textAlign: 'right' }}>Irregularidade</th></tr></thead>
              <tbody>
                {dados.calibracao.map((c) => {
                  const r = reguas.find((x) => x.id === c.regua_id);
                  return (
                    <tr key={c.regua_id}><td style={td}>{r?.nome || c.regua_id}</td><td style={num}>{c.casos}</td><td style={num}>{c.novos}</td><td style={num}>{c.balizados}</td><td style={num}>{c.finalizados}</td><td style={num}>{c.justificados}</td><td style={num}>{c.irregulares}</td></tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Últimas execuções</div>
          {(dados.ultimas_execucoes || []).length === 0 ? <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>Nenhuma.</div> : (
            <div style={{ fontSize: '0.78rem' }}>
              {dados.ultimas_execucoes.map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', borderBottom: '1px solid #f5f5f5' }}>
                  {e.ok ? <CheckCircle2 size={13} color="#1e8449" /> : <AlertTriangle size={13} color="#c0392b" />}
                  <span>{new Date(e.dt_execucao).toLocaleString('pt-BR')}</span>
                  <span style={{ color: 'var(--gray-500)' }}>{e.origem} · {e.mes ? mesLabel(String(e.mes).slice(0, 7)) : ''} · {e.qt_casos} caso(s), {e.qt_cards} tarefa(s)</span>
                  {e.erro && <span style={{ color: '#c0392b' }}>{e.erro}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modal && (
        <ModalRegua regua={modal.id ? modal : null} medidas={dados.medidas || []} setores={setores} tipos={tipos}
          onClose={() => setModal(null)} onSalvo={() => { setModal(null); carregar(); }} />
      )}
    </div>
  );
}
